import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Input,
  Select,
  Spin,
  Tooltip,
  Button,
  Badge,
  Card,
  Row,
  Col,
  Space,
  Typography,
  List,
  Tag,
  Dropdown,
  Divider,
  message
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  LeftOutlined,
  RightOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  TeamOutlined,
  ShopOutlined,
  ShoppingOutlined,
  SolutionOutlined,
  PlusOutlined,
  MoreOutlined,
  FilterOutlined
} from '@ant-design/icons';
import LayoutPage from "../../layouts/Main";
import UserService from "../../utils/api/users";
import EditUserModal from "./EditUserModal";
import '../../scss/AdminClientes.scss';

const { Option } = Select;
const { Title, Text } = Typography;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const MobileClientCard = ({ client, onAssign, onRemove, tagColor, tagText }) => {
  const getClientMenuItems = () => [
    {
      key: 'assign-client',
      label: 'Asignar cliente',
      icon: <PlusOutlined />,
      onClick: onAssign
    },
    {
      key: 'remove-client',
      label: 'Remover cliente',
      danger: true,
      onClick: onRemove
    },
  ];

  return (
    <div className="mobile-card-item">
      <div className="mobile-card-header">
        <h4>{client.name}</h4>
        <div className="mobile-card-actions">
          <Dropdown
            menu={{ items: getClientMenuItems() }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        </div>
      </div>

      <div className="mobile-card-body">
        <div className="info-row">
          <MailOutlined className="info-icon" />
          <span className="info-content">{client.email}</span>
        </div>
        <div className="info-row">
          <PhoneOutlined className="info-icon" />
          <span className="info-content">{client.phone || 'Sin teléfono'}</span>
        </div>
        <div className="info-row">
          <ShopOutlined className="info-icon" />
          <span className="info-content">{client.bussiness || 'Sin empresa'}</span>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="tags-container">
          <Tag color="purple" style={{ fontSize: '11px' }}>CLIENTE</Tag>
          <Tag color="success" style={{ fontSize: '11px' }}>ACTIVO</Tag>
          <Tag color={tagColor} style={{ fontSize: '11px' }}>{tagText}</Tag>
        </div>
      </div>
    </div>
  );
};

const MobilePersonnelCard = ({ item, type, onAssign, onRemove, tagColor, tagText }) => {
  const getMenuItems = () => {
    const role = type === 'seller' ? 'vendedor' : 'comprador';
    return [
      {
        key: 'assign',
        label: `Asignar ${role}`,
        icon: <PlusOutlined />,
        onClick: onAssign
      },
      {
        key: 'remove',
        label: `Remover ${role}`,
        danger: true,
        onClick: onRemove
      }
    ];
  };

  return (
    <div className="mobile-card-item">
      <div className="mobile-card-header">
        <h4>{item.name}</h4>
        <div className="mobile-card-actions">
          <Dropdown menu={{ items: getMenuItems() }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        </div>
      </div>

      <div className="mobile-card-body">
        <div className="info-row">
          <MailOutlined className="info-icon" />
          <span className="info-content">{item.email}</span>
        </div>
        <div className="info-row">
          <PhoneOutlined className="info-icon" />
          <span className="info-content">{item.phone || 'Sin teléfono'}</span>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="tags-container">
          <Tag color={tagColor} style={{ fontSize: '11px' }}>{tagText}</Tag>
          <Tag color="success" style={{ fontSize: '11px' }}>ACTIVO</Tag>
        </div>
      </div>
    </div>
  );
};

export default function AdminClientes() {
  const [selectedUserType, setSelectedUserType] = useState('todos');
  const [searchText, setSearchText] = useState('');
  const [expandedRows, setExpandedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [assignedClients, setAssignedClients] = useState({});
  const [assignedPersonnel, setAssignedPersonnel] = useState({});
  const [loadingClients, setLoadingClients] = useState({});
  const [loadingPersonnel, setLoadingPersonnel] = useState({});
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [allRelatedUsersCache, setAllRelatedUsersCache] = useState(null);
  const [isInitialCacheLoaded, setIsInitialCacheLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const tableContainerRef = useRef(null);
  const isMobile = useIsMobile();

  const roleMapping = {
    "1": "Administrador",
    "2": "Finanzas internas",
    "3": "Comprador",
    "4": "Cliente",
    "5": "Vendedor",
    "6": "Proveedor",
    "9": "Repartidor"
  };

  const targetRoles = ["3", "4", "5"];

  const scrollToTop = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  const loadCurrentAdmin = useCallback(() => {
    try {
      const userData =
        localStorage.getItem('currentUser') ||
        localStorage.getItem('user') ||
        localStorage.getItem('adminUser') ||
        localStorage.getItem('loggedUser');

      if (userData) {
        const parsedUser = JSON.parse(userData);
        setCurrentAdmin(parsedUser);
      }
    } catch (error) {
      console.error("Error cargando admin data:", error);
    }
  }, []);

  const loadInitialRelatedUsersCache = useCallback(async () => {
    if (isInitialCacheLoaded && allRelatedUsersCache) {
      return allRelatedUsersCache;
    }

    try {
      const [sellersResponse, buyersResponse, clientsResponse] = await Promise.all([
        UserService.GetClientsAdmin({ role: '5', limit: 1000 }),
        UserService.GetClientsAdmin({ role: '3', limit: 1000 }),
        UserService.GetClientsAdmin({ role: '4', limit: 1000 })
      ]);

      const relatedUsers = {
        '5': sellersResponse.data?.success ? sellersResponse.data.users || [] : [],
        '3': buyersResponse.data?.success ? buyersResponse.data.users || [] : [],
        '4': clientsResponse.data?.success ? clientsResponse.data.users || [] : []
      };

      setAllRelatedUsersCache(relatedUsers);
      setIsInitialCacheLoaded(true);

      return relatedUsers;
    } catch (error) {
      const emptyCache = { '5': [], '3': [], '4': [] };
      setAllRelatedUsersCache(emptyCache);
      setIsInitialCacheLoaded(true);
      return emptyCache;
    }
  }, [isInitialCacheLoaded, allRelatedUsersCache]);

  const processUserAssignments = useCallback((usersToProcess) => {
    if (!usersToProcess || usersToProcess.length === 0 || !allRelatedUsersCache) {
      return { assignedClientsData: {}, assignedPersonnelData: {} };
    }

    const assignedClientsData = {};
    const assignedPersonnelData = {};

    const allSellers = allRelatedUsersCache['5'] || [];
    const allBuyers = allRelatedUsersCache['3'] || [];
    const allClients = allRelatedUsersCache['4'] || [];

    usersToProcess.forEach(user => {
      const userId = user.id;
      const userRole = user.role?.toString();

      if (userRole === "5") {
        const userClients = allClients.filter(client =>
          client.assigned_sellers &&
          Array.isArray(client.assigned_sellers) &&
          client.assigned_sellers.includes(userId)
        );

        if (userClients.length > 0) {
          assignedClientsData[userId] = userClients;
        }
      }
      else if (userRole === "3") {
        const userClients = allClients.filter(client =>
          client.assigned_buyers &&
          Array.isArray(client.assigned_buyers) &&
          client.assigned_buyers.includes(userId)
        );

        if (userClients.length > 0) {
          assignedClientsData[userId] = userClients;
        }
      }
      else if (userRole === "4") {
        const client = user;

        const clientSellers = allSellers.filter(seller =>
          client.assigned_sellers &&
          Array.isArray(client.assigned_sellers) &&
          client.assigned_sellers.includes(seller.id)
        );

        const clientBuyers = allBuyers.filter(buyer =>
          client.assigned_buyers &&
          Array.isArray(client.assigned_buyers) &&
          client.assigned_buyers.includes(buyer.id)
        );

        if (clientSellers.length > 0 || clientBuyers.length > 0) {
          assignedPersonnelData[userId] = {
            sellers: clientSellers,
            buyers: clientBuyers
          };
        }
      }
    });

    return { assignedClientsData, assignedPersonnelData };
  }, [allRelatedUsersCache]);

  const loadPageData = useCallback(async (pageNumber, direction = "next", search = null, userType = null, forceReload = false) => {
    if (loading && !forceReload) return;

    setLoading(true);

    try {
      let cursor = null;
      let attempts = 0;
      let collectedUsers = [];
      const batchLimit = 200;

      while (attempts < 100) {
        const response = await UserService.GetClientsAdmin({
          limit: batchLimit,
          lastVisible: cursor
        });

        if (!(response.data && response.data.success)) {
          break;
        }

        const batchUsers = response.data.users || [];
        const nextCursor = response.data.nextCursor || response.data.nextPage || null;

        if (batchUsers.length > 0) {
          collectedUsers = [...collectedUsers, ...batchUsers];
        }

        if (!nextCursor || batchUsers.length === 0 || nextCursor === cursor) {
          break;
        }

        cursor = nextCursor;
        attempts += 1;
      }

      const usersMap = new Map();
      collectedUsers.forEach((user) => {
        if (user?.id) {
          usersMap.set(user.id, user);
        }
      });

      const allUsers = Array.from(usersMap.values());
      setUsers(allUsers);

      if (isInitialCacheLoaded) {
        const { assignedClientsData, assignedPersonnelData } =
          processUserAssignments(allUsers);

        setAssignedClients(assignedClientsData);
        setAssignedPersonnel(assignedPersonnelData);
      }

      setCurrentPage(1);
    } catch (error) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [loading, isInitialCacheLoaded, processUserAssignments]);

  useEffect(() => {
    if (isInitialized) return;

    const initialize = async () => {
      try {
        loadCurrentAdmin();
        await loadInitialRelatedUsersCache();
        await loadPageData(1, "next", null, selectedUserType, true);
        setIsInitialized(true);
      } catch (error) {
        setIsInitialized(true);
      }
    };

    initialize();
  }, [isInitialized, loadCurrentAdmin, loadInitialRelatedUsersCache, loadPageData, selectedUserType]);

  const loadAssignedClients = async (user) => {
    const userId = user.id;
    const userRole = user.role?.toString();

    if (allRelatedUsersCache && allRelatedUsersCache['4']) {
      const allClients = allRelatedUsersCache['4'];
      const userClients = allClients.filter(client => {
        if (userRole === "5") {
          return client.assigned_sellers?.includes(userId);
        } else if (userRole === "3") {
          return client.assigned_buyers?.includes(userId);
        }
        return false;
      });

      if (userClients.length > 0 || assignedClients[userId]) {
        setAssignedClients(prev => ({
          ...prev,
          [userId]: userClients
        }));
        return;
      }
    }

    if (assignedClients[userId]) return;

    try {
      setLoadingClients(prev => ({ ...prev, [userId]: true }));

      let response;
      if (userRole === "5") {
        response = await UserService.GetClientsBySeller(userId);
      } else if (userRole === "3") {
        response = await UserService.GetClientsByBuyer(userId);
      } else {
        return;
      }

      if (response && Array.isArray(response)) {
        setAssignedClients(prev => ({
          ...prev,
          [userId]: response
        }));
      }
    } catch (error) {
    } finally {
      setLoadingClients(prev => ({ ...prev, [userId]: false }));
    }
  };

  const loadAssignedPersonnel = async (client) => {
    const clientId = client.id;

    if (assignedPersonnel[clientId]) return;

    try {
      setLoadingPersonnel(prev => ({ ...prev, [clientId]: true }));
      const response = await UserService.GetAssignedPersonnel(clientId);

      if (response.data) {
        setAssignedPersonnel(prev => ({
          ...prev,
          [clientId]: {
            sellers: response.data.sellers || [],
            buyers: response.data.buyers || []
          }
        }));
      }
    } catch (error) {
    } finally {
      setLoadingPersonnel(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const handleRemoveAssignment = async (sourceUser, targetUser, relationshipType) => {
    if (!currentAdmin?.id) {
      message.error('Error: No se pudo identificar al administrador');
      return;
    }

    try {
      setLoading(true);
      const sourceRole = sourceUser.role?.toString();

      if (sourceRole === "3" || sourceRole === "5") {
        const assignmentData = {
          clientId: targetUser.id,
          adminId: currentAdmin.id,
          addBuyers: [],
          addSellers: [],
          removeBuyers: sourceRole === "3" ? [sourceUser.id] : [],
          removeSellers: sourceRole === "5" ? [sourceUser.id] : []
        };

        await UserService.AssignClientsToPersonnel(assignmentData);

        setAssignedClients(prev => {
          const updated = { ...prev };
          if (updated[sourceUser.id]) {
            updated[sourceUser.id] = updated[sourceUser.id].filter(
              client => client.id !== targetUser.id
            );
          }
          return updated;
        });

        setAllRelatedUsersCache(prevCache => {
          if (!prevCache) return prevCache;

          const newCache = { ...prevCache };
          const clients = [...(newCache['4'] || [])];

          const clientIndex = clients.findIndex(c => c.id === targetUser.id);
          if (clientIndex >= 0) {
            const client = { ...clients[clientIndex] };

            if (sourceRole === "5") {
              client.assigned_sellers = (client.assigned_sellers || []).filter(id => id !== sourceUser.id);
            } else if (sourceRole === "3") {
              client.assigned_buyers = (client.assigned_buyers || []).filter(id => id !== sourceUser.id);
            }

            clients[clientIndex] = client;
          }

          newCache['4'] = clients;
          return newCache;
        });

        message.success(`Cliente removido exitosamente del ${sourceRole === "3" ? 'comprador' : 'vendedor'}`);

      } else if (sourceRole === "4") {
        const targetRole = targetUser.role?.toString();
        const assignmentData = {
          clientId: sourceUser.id,
          adminId: currentAdmin.id,
          addBuyers: [],
          addSellers: [],
          removeBuyers: targetRole === "3" ? [targetUser.id] : [],
          removeSellers: targetRole === "5" ? [targetUser.id] : []
        };

        await UserService.AssignClientsToPersonnel(assignmentData);

        setAssignedPersonnel(prev => {
          const updated = { ...prev };
          if (updated[sourceUser.id]) {
            const personnel = updated[sourceUser.id];
            if (targetRole === "3") {
              personnel.buyers = personnel.buyers.filter(b => b.id !== targetUser.id);
            } else if (targetRole === "5") {
              personnel.sellers = personnel.sellers.filter(s => s.id !== targetUser.id);
            }
            updated[sourceUser.id] = personnel;
          }
          return updated;
        });

        message.success(`${targetRole === "3" ? 'Comprador' : 'Vendedor'} removido exitosamente del cliente`);
      }

      setExpandedRows(prev => prev.filter(id => id !== sourceUser.id));

    } catch (error) {
      message.error(error.response?.data?.error || 'Error al remover la asignación');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setCurrentPage(1);
    setIsSearching(false);
  }, []);

  const handleSearch = useCallback(() => {
    const trimmedSearch = searchText.trim();

    if (trimmedSearch === "") {
      handleClearSearch();
      return;
    }

    setSearchText(trimmedSearch);
    setCurrentPage(1);
    setIsSearching(true);
  }, [searchText, handleClearSearch]);

  const handleNextPage = () => {
    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      scrollToTop();
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      scrollToTop();
    }
  };

  const getDisplayRange = () => {
    if (filteredData.length === 0) {
      return { start: 0, end: 0 };
    }
    const start = ((currentPage - 1) * pageSize) + 1;
    const end = Math.min(currentPage * pageSize, filteredData.length);
    return { start, end };
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    scrollToTop();
  };

  const handleEditIconClick = (user, event) => {
    if (event) event.stopPropagation();
    setUserToEdit(user);
    setEditModalVisible(true);
  };

  const handleSaveEdit = (data) => {
    setEditModalVisible(false);
    setUserToEdit(null);

    if (data && data.success) {
      const { userRole, userId, usersToAdd, usersToRemove, assignedUsers } = data;

      if (userRole === "3" || userRole === "5") {

        setAllRelatedUsersCache(prevCache => {
          if (!prevCache) return prevCache;

          const newCache = { ...prevCache };
          const clients = [...(newCache['4'] || [])];

          const affectedClientIds = [...usersToAdd, ...usersToRemove];

          affectedClientIds.forEach(clientId => {
            const clientIndex = clients.findIndex(c => c.id === clientId);
            if (clientIndex >= 0) {
              const client = { ...clients[clientIndex] };

              if (userRole === "5") { // Vendedor
                if (usersToAdd.includes(clientId)) {
                  client.assigned_sellers = client.assigned_sellers || [];
                  if (!client.assigned_sellers.includes(userId)) {
                    client.assigned_sellers.push(userId);
                  }
                } else if (usersToRemove.includes(clientId)) {
                  client.assigned_sellers = (client.assigned_sellers || []).filter(id => id !== userId);
                }
              } else if (userRole === "3") { // Comprador
                if (usersToAdd.includes(clientId)) {
                  client.assigned_buyers = client.assigned_buyers || [];
                  if (!client.assigned_buyers.includes(userId)) {
                    client.assigned_buyers.push(userId);
                  }
                } else if (usersToRemove.includes(clientId)) {
                  client.assigned_buyers = (client.assigned_buyers || []).filter(id => id !== userId);
                }
              }

              clients[clientIndex] = client;
            }
          });

          newCache['4'] = clients;
          return newCache;
        });

        setTimeout(() => {
          setAllRelatedUsersCache(prevCache => {
            if (!prevCache) return prevCache;

            const allClients = prevCache['4'] || [];
            const userAssignedClients = allClients.filter(client => {
              if (userRole === "5") {
                return client.assigned_sellers?.includes(userId);
              } else if (userRole === "3") {
                return client.assigned_buyers?.includes(userId);
              }
              return false;
            });

            setAssignedClients(prev => ({
              ...prev,
              [userId]: userAssignedClients
            }));

            return prevCache;
          });
        }, 100);

      } else if (userRole === "4") {

        if (assignedUsers && assignedUsers.length > 0) {
          const sellers = assignedUsers.filter(u => u.role?.toString() === "5");
          const buyers = assignedUsers.filter(u => u.role?.toString() === "3");

          setAssignedPersonnel(prev => ({
            ...prev,
            [userId]: {
              sellers: sellers,
              buyers: buyers
            }
          }));
        } else {
          setAssignedPersonnel(prev => ({
            ...prev,
            [userId]: {
              sellers: [],
              buyers: []
            }
          }));
        }

        setAllRelatedUsersCache(prevCache => {
          if (!prevCache) return prevCache;

          const newCache = { ...prevCache };

          const clientIndex = newCache['4']?.findIndex(c => c.id === userId) ?? -1;
          if (clientIndex >= 0 && newCache['4']) {
            const updatedClient = {
              ...newCache['4'][clientIndex],
              assigned_sellers: assignedUsers?.filter(u => u.role?.toString() === "5").map(u => u.id) || [],
              assigned_buyers: assignedUsers?.filter(u => u.role?.toString() === "3").map(u => u.id) || []
            };
            newCache['4'][clientIndex] = updatedClient;
          }

          return newCache;
        });
      }
    }

    setExpandedRows([]);
    setUsers(prevUsers => [...prevUsers]);
  };

  const handleCloseEditModal = () => {
    setEditModalVisible(false);
    setUserToEdit(null);
  };

  const getTypeBadgeClass = (roleNumber) => {
    const roleMap = {
      "3": "type-badge comprador",
      "4": "type-badge cliente",
      "5": "type-badge vendedor"
    };
    return roleMap[roleNumber] || "type-badge";
  };

  const getStatusBadgeClass = (status) => {
    if (status === "ACTIVO") return "status-badge activo";
    if (status === "INACTIVO") return "status-badge inactivo";
    return "status-badge desconocido";
  };

  const getRoleLabel = (role) => {
    const roleStr = role?.toString();
    return roleMapping[roleStr] || `Rol ${role}`;
  };

  const getRoleIcon = (roleNumber) => {
    const iconMap = {
      "3": <ShoppingOutlined />,
      "4": <ShopOutlined />,
      "5": <SolutionOutlined />,
      "9": <TeamOutlined />
    };
    return iconMap[roleNumber] || <UserOutlined />;
  };

  const getTooltipText = (user) => {
    const userRole = user.role?.toString();
    if (userRole === "5" || userRole === "3") {
      return "Click para mostrar clientes asignados";
    } else if (userRole === "4") {
      return "Click para mostrar vendedores/compradores asignados";
    }
    return "Click para mostrar detalles";
  };

  const renderExpandedContent = (user) => {
    const userRole = user.roleNumber;
    const userId = user.key;

    if (userRole === "5" || userRole === "3") {
      return renderClientsContent(userId, userRole === "5" ? "vendedor" : "comprador");
    } else if (userRole === "4") {
      return renderPersonnelContent(userId);
    }

    return null;
  };

  const renderClientsContent = (userId, userType) => {
    const clients = assignedClients[userId] || [];
    const isLoading = loadingClients[userId];
    const isVendedor = userType === "vendedor";
    const icon = isVendedor ? <SolutionOutlined /> : <ShoppingOutlined />;
    const color = isVendedor ? '#52c41a' : '#1890ff';
    const tagColor = isVendedor ? "green" : "blue";
    const tagText = isVendedor ? "VENDEDOR" : "COMPRADOR";
    const currentUser = users.find(u => u.id === userId);

    const getClientMenuItems = (client) => [
      {
        key: 'assign-client',
        label: 'Asignar cliente',
        icon: <PlusOutlined />,
        onClick: () => handleEditIconClick(currentUser)
      },
      {
        key: 'remove-client',
        label: 'Remover cliente',
        danger: true,
        onClick: () => handleRemoveAssignment(currentUser, client, 'cliente')
      },
    ];

    return (
      <div className="expanded-row">
        <div className="expanded-content">
          <Card size="small" bordered={false} className="row-details-card">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div className="personnel-section">
                <div className="section-header">
                  <Row align="middle" justify="space-between" style={{ width: '100%' }}>
                    <Col>
                      <Space align="center" size="middle">
                        {icon}
                        <Title level={5} style={{ margin: 0 }}>
                          Clientes asignados
                          <Badge
                            count={clients.length}
                            showZero
                            style={{ marginLeft: 8, backgroundColor: color }}
                          />
                        </Title>
                      </Space>
                    </Col>
                  </Row>
                </div>

                <div className="section-content">
                  {isLoading ? (
                    <div style={{ padding: '20px 0', textAlign: 'center' }}>
                      <Spin size="small" />
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                        Cargando clientes...
                      </Text>
                    </div>
                  ) : (
                    <>
                      {clients.length > 0 ? (
                        <>
                          <List
                            className="desktop-only"
                            size="small"
                            dataSource={clients}
                            renderItem={(client) => (
                              <List.Item
                                actions={[
                                  <Dropdown
                                    menu={{ items: getClientMenuItems(client) }}
                                    trigger={['click']}
                                  >
                                    <Button type="text" icon={<MoreOutlined />} size="small" />
                                  </Dropdown>
                                ]}
                              >
                                <List.Item.Meta
                                  title={
                                    <Text strong style={{ fontSize: '13px' }}>
                                      {client.name}
                                    </Text>
                                  }
                                  description={
                                    <Space direction="vertical" size={0}>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <MailOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                                        {client.email}
                                      </Text>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <PhoneOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                                        {client.phone || 'Sin teléfono'}
                                      </Text>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <ShopOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                                        {client.bussiness || 'Sin empresa'}
                                      </Text>
                                    </Space>
                                  }
                                />
                                <Space size="small" style={{ flexWrap: 'wrap' }}>
                                  <Tag color="purple" style={{ fontSize: '10px', padding: '1px 6px', margin: '2px 0' }}>CLIENTE</Tag>
                                  <Tag color="success" style={{ fontSize: '10px', padding: '1px 6px', margin: '2px 0' }}>ACTIVO</Tag>
                                  <Tag color={tagColor} style={{ fontSize: '10px', padding: '1px 6px', margin: '2px 0' }}>
                                    {tagText}
                                  </Tag>
                                </Space>
                              </List.Item>
                            )}
                          />

                          <div className="horizontal-scroll-container mobile-only">
                            {clients.map(client => (
                              <MobileClientCard
                                key={client.id}
                                client={client}
                                currentUser={currentUser}
                                tagColor={tagColor}
                                tagText={tagText}
                                getClientMenuItems={getClientMenuItems}
                              />
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="no-data-message">
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', textAlign: 'center', padding: '8px 0' }}>
                            No hay clientes asignados
                          </Text>
                          <div style={{ textAlign: 'center', marginTop: '8px' }}>
                            <Button
                              type="link"
                              size="small"
                              onClick={() => handleEditIconClick(currentUser)}
                              style={{ fontSize: '12px', padding: '0' }}
                            >
                              Asignar cliente
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Space>
          </Card>
        </div>
      </div>
    );
  };

  const renderPersonnelContent = (userId) => {
    const personnel = assignedPersonnel[userId] || { sellers: [], buyers: [] };
    const isLoading = loadingPersonnel[userId];
    const currentClient = users.find(u => u.id === userId);

    const getSellerMenuItems = (seller) => [
      {
        key: 'assign-seller',
        label: 'Asignar vendedor',
        icon: <PlusOutlined />,
        onClick: () => handleEditIconClick(currentClient)
      },
      {
        key: 'remove-seller',
        label: 'Remover vendedor',
        danger: true,
        onClick: () => handleRemoveAssignment(currentClient, seller, 'vendedor')
      },
    ];

    const getBuyerMenuItems = (buyer) => [
      {
        key: 'assign-buyer',
        label: 'Asignar comprador',
        icon: <PlusOutlined />,
        onClick: () => handleEditIconClick(currentClient)
      },
      {
        key: 'remove-buyer',
        label: 'Remover comprador',
        danger: true,
        onClick: () => handleRemoveAssignment(currentClient, buyer, 'comprador')
      },
    ];

    return (
      <div className="expanded-row">
        <div className="expanded-content">
          <Card size="small" bordered={false} className="row-details-card">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div className="personnel-section">
                <div className="section-header">
                  <Row align="middle" justify="space-between" style={{ width: '100%' }}>
                    <Col>
                      <Space align="center" size="middle">
                        <SolutionOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                        <Title level={5} style={{ margin: 0 }}>
                          Vendedores asignados
                          <Badge
                            count={personnel.sellers?.length || 0}
                            showZero
                            style={{ marginLeft: 8, backgroundColor: '#52c41a' }}
                          />
                        </Title>
                      </Space>
                    </Col>
                  </Row>
                </div>

                <div className="section-content">
                  {isLoading ? (
                    <div style={{ padding: '20px 0', textAlign: 'center' }}>
                      <Spin size="small" />
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                        Cargando vendedores...
                      </Text>
                    </div>
                  ) : (
                    <>
                      {personnel.sellers && personnel.sellers.length > 0 ? (
                        <>
                          <List
                            className="desktop-only"
                            size="small"
                            dataSource={personnel.sellers}
                            renderItem={(seller) => (
                              <List.Item
                                actions={[
                                  <Dropdown
                                    menu={{ items: getSellerMenuItems(seller) }}
                                    trigger={['click']}
                                  >
                                    <Button type="text" icon={<MoreOutlined />} size="small" />
                                  </Dropdown>
                                ]}
                              >
                                <List.Item.Meta
                                  title={
                                    <Text strong style={{ fontSize: '13px' }}>
                                      {seller.name}
                                    </Text>
                                  }
                                  description={
                                    <Space direction="vertical" size={0}>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <MailOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                                        {seller.email}
                                      </Text>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <PhoneOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                                        {seller.phone || 'Sin teléfono'}
                                      </Text>
                                    </Space>
                                  }
                                />
                                <Space size="small" style={{ flexWrap: 'wrap' }}>
                                  <Tag color="green" style={{ fontSize: '10px', padding: '1px 6px', margin: '2px 0' }}>VENDEDOR</Tag>
                                  <Tag color="success" style={{ fontSize: '10px', padding: '1px 6px', margin: '2px 0' }}>ACTIVO</Tag>
                                </Space>
                              </List.Item>
                            )}
                          />

                          <div className="horizontal-scroll-container mobile-only">
                            {personnel.sellers.map(seller => (
                              <MobilePersonnelCard
                                key={seller.id}
                                item={seller}
                                type="seller"
                                onAssign={() => handleEditIconClick(currentClient)}
                                onRemove={() => handleRemoveAssignment(currentClient, seller, 'vendedor')}
                                tagColor="green"
                                tagText="VENDEDOR"
                              />
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="no-data-message">
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', textAlign: 'center', padding: '8px 0' }}>
                            No hay vendedores asignados
                          </Text>
                          <div style={{ textAlign: 'center', marginTop: '8px' }}>
                            <Button
                              type="link"
                              size="small"
                              onClick={() => handleEditIconClick(currentClient)}
                              style={{ fontSize: '12px', padding: '0' }}
                            >
                              Asignar vendedor
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div className="personnel-section">
                <div className="section-header">
                  <Row align="middle" justify="space-between" style={{ width: '100%' }}>
                    <Col>
                      <Space align="center" size="middle">
                        <ShoppingOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                        <Title level={5} style={{ margin: 0 }}>
                          Compradores asignados
                          <Badge
                            count={personnel.buyers?.length || 0}
                            showZero
                            style={{ marginLeft: 8, backgroundColor: '#1890ff' }}
                          />
                        </Title>
                      </Space>
                    </Col>
                  </Row>
                </div>

                <div className="section-content">
                  {isLoading ? (
                    <div style={{ padding: '20px 0', textAlign: 'center' }}>
                      <Spin size="small" />
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                        Cargando compradores...
                      </Text>
                    </div>
                  ) : (
                    <>
                      {personnel.buyers && personnel.buyers.length > 0 ? (
                        <>
                          <List
                            className="desktop-only"
                            size="small"
                            dataSource={personnel.buyers}
                            renderItem={(buyer) => (
                              <List.Item
                                actions={[
                                  <Dropdown
                                    menu={{ items: getBuyerMenuItems(buyer) }}
                                    trigger={['click']}
                                  >
                                    <Button type="text" icon={<MoreOutlined />} size="small" />
                                  </Dropdown>
                                ]}
                              >
                                <List.Item.Meta
                                  title={
                                    <Text strong style={{ fontSize: '13px' }}>
                                      {buyer.name}
                                    </Text>
                                  }
                                  description={
                                    <Space direction="vertical" size={0}>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <MailOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                                        {buyer.email}
                                      </Text>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <PhoneOutlined style={{ marginRight: 4, fontSize: '10px' }} />
                                        {buyer.phone || 'Sin teléfono'}
                                      </Text>
                                    </Space>
                                  }
                                />
                                <Space size="small" style={{ flexWrap: 'wrap' }}>
                                  <Tag color="blue" style={{ fontSize: '10px', padding: '1px 6px', margin: '2px 0' }}>COMPRADOR</Tag>
                                  <Tag color="success" style={{ fontSize: '10px', padding: '1px 6px', margin: '2px 0' }}>ACTIVO</Tag>
                                </Space>
                              </List.Item>
                            )}
                          />

                          <div className="horizontal-scroll-container mobile-only">
                            {personnel.buyers.map(buyer => (
                              <MobilePersonnelCard
                                key={buyer.id}
                                item={buyer}
                                type="buyer"
                                onAssign={() => handleEditIconClick(currentClient)}
                                onRemove={() => handleRemoveAssignment(currentClient, buyer, 'comprador')}
                                tagColor="blue"
                                tagText="COMPRADOR"
                              />
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="no-data-message">
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', textAlign: 'center', padding: '8px 0' }}>
                            No hay compradores asignados
                          </Text>
                          <div style={{ textAlign: 'center', marginTop: '8px' }}>
                            <Button
                              type="link"
                              size="small"
                              onClick={() => handleEditIconClick(currentClient)}
                              style={{ fontSize: '12px', padding: '0' }}
                            >
                              Asignar comprador
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Space>
          </Card>
        </div>
      </div>
    );
  };

  const handleUserTypeChange = (value) => {
    setSelectedUserType(value);
    setCurrentPage(1);
    setExpandedRows([]);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setIsSearching(value.trim() !== "");
    setCurrentPage(1);

    if (value === "" && isSearching) {
      handleClearSearch();
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const data = useMemo(() => {
    return users.map((user) => {
      const userStatus = user.status;
      let statusText = "ACTIVO";

      if (userStatus === true || userStatus === "true" || userStatus === 1) {
        statusText = "ACTIVO";
      } else if (userStatus === false || userStatus === "false" || userStatus === 0) {
        statusText = "INACTIVO";
      } else if (userStatus === undefined || userStatus === null) {
        statusText = "ACTIVO";
      }

      const userRole = user.role?.toString();
      const roleName = roleMapping[userRole] || `Rol ${userRole}`;

      return {
        key: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone || "No disponible",
        type: roleName,
        roleNumber: userRole,
        status: statusText,
        statusBool: user.status,
        userData: user
      };
    });
  }, [users]);

  const filteredData = useMemo(() => {
    return data.filter(user => {
      const userRole = user.roleNumber;

      if (!targetRoles.includes(userRole)) {
        return false;
      }

      if (searchText && searchText.trim() !== "") {
        const searchLower = searchText.toLowerCase().trim();
        const matchesSearch =
          user.name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.phone?.toLowerCase().includes(searchLower);

        if (!matchesSearch) {
          return false;
        }
      }

      if (selectedUserType !== 'todos') {
        const roleMap = {
          'vendedor': '5',
          'cliente': '4',
          'comprador': '3'
        };

        const targetRole = roleMap[selectedUserType];
        if (targetRole && userRole !== targetRole) {
          return false;
        }
      }

      return true;
    });
  }, [data, selectedUserType, searchText]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredData.length, pageSize, currentPage]);

  const displayRange = getDisplayRange();

  const toggleExpand = async (userId) => {
    const user = users.find(u => u.id === userId);

    if (user) {
      const userRole = user.role?.toString();

      if (userRole === "5" || userRole === "3") {
        if (!expandedRows.includes(userId)) {
          await loadAssignedClients(user);
        }
      } else if (userRole === "4") {
        if (!expandedRows.includes(userId)) {
          await loadAssignedPersonnel(user);
        }
      }
    }

    setExpandedRows(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleRowClick = async (userId) => {
    await toggleExpand(userId);
  };

  if (!isInitialized || !isInitialCacheLoaded) {
    return (
      <LayoutPage>
        <div className="admin-users-container">
          <div className="orders-card">
            <div className="loading-container">
              <Spin size="large" />
              <p>Inicializando sistema...</p>
            </div>
          </div>
        </div>
      </LayoutPage>
    );
  }

  if (loading && users.length === 0) {
    return (
      <LayoutPage>
        <div className="admin-users-container">
          <div className="orders-card">
            <div className="loading-container">
              <Spin size="large" />
              <p>Cargando usuarios...</p>
            </div>
          </div>
        </div>
      </LayoutPage>
    );
  }

  return (
    <LayoutPage>
      <div className="admin-users-container">
        <div className="orders-card">
          <div className="table-header-section">
            <Title level={2}>Administración de Clientes</Title>
            {currentAdmin && (
              <div style={{ color: '#666', fontSize: '14px', marginTop: '8px', marginBottom: '16px' }}>
                Conectado como: <strong>{currentAdmin.name}</strong> ({getRoleLabel(currentAdmin.role)})
              </div>
            )}

            <div className="filters-section">
              <div className="search-filter">
                <Input
                  className="simple-search"
                  placeholder="Buscar por nombre..."
                  value={searchText}
                  onChange={handleSearchChange}
                  onPressEnter={handleSearchKeyPress}
                  prefix={<SearchOutlined />}
                  size="large"
                  allowClear
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="table-container" ref={tableContainerRef}>
            <div className="styled-table custom-table">
              <div className="table-header">
                <div className="header-row">
                  <div className="header-cell nombre">Nombre</div>
                  <div className="header-cell email">Email</div>
                  <div className="header-cell telefono">Teléfono</div>
                  <div className="header-cell tipo">
                    <span>Tipo</span>
                    <Tooltip title="Selecciona un tipo de usuario" placement="top">
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'todos',
                              label: 'Todos los tipos',
                              onClick: () => handleUserTypeChange('todos')
                            },
                            { type: 'divider' },
                            {
                              key: 'vendedor',
                              label: 'Vendedores',
                              icon: <SolutionOutlined />,
                              onClick: () => handleUserTypeChange('vendedor')
                            },
                            {
                              key: 'cliente',
                              label: 'Clientes',
                              icon: <ShopOutlined />,
                              onClick: () => handleUserTypeChange('cliente')
                            },
                            {
                              key: 'comprador',
                              label: 'Compradores',
                              icon: <ShoppingOutlined />,
                              onClick: () => handleUserTypeChange('comprador')
                            }
                          ],
                          selectedKeys: [selectedUserType]
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                      >
                        <Button
                          type="text"
                          icon={<FilterOutlined />}
                          className={`filter-button ${selectedUserType !== 'todos' ? 'active' : ''}`}
                          size="small"
                        />
                      </Dropdown>
                    </Tooltip>
                  </div>
                  <div className="header-cell estatus">Estatus</div>
                  <div className="header-cell accion">Acciones</div>
                </div>
              </div>

              <div className="table-body">
                {loading && users.length === 0 ? (
                  <div className="empty-state">
                    <Spin size="small" />
                    <p>Cargando usuarios...</p>
                  </div>
                ) : filteredData.length === 0 ? (
                  <div className="empty-state">
                    {isSearching ? (
                      <div>
                        <p>No se encontraron usuarios que coincidan con "{searchText}"</p>
                        <Button
                          type="link"
                          onClick={handleClearSearch}
                          style={{ marginTop: '8px' }}
                        >
                          Mostrar todos los usuarios
                        </Button>
                      </div>
                    ) : (
                      <p>No hay usuarios disponibles</p>
                    )}
                  </div>
                ) : (
                  paginatedData.map(user => {
                    const userRole = user.roleNumber;

                    return (
                      <React.Fragment key={user.key}>
                        <Tooltip title={getTooltipText(user.userData)} placement="top" mouseEnterDelay={0.5}>
                          <div className="table-row clickable-row" onClick={() => handleRowClick(user.key)}>
                            <div className="body-row">
                              <div className="body-cell nombre" data-label="Nombre">
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span className="user-name">{user.name || 'Nombre no disponible'}</span>
                                  {(userRole === "5" || userRole === "3") && (
                                    <div className="clients-count">
                                      {assignedClients[user.key] ? assignedClients[user.key].length : '0'} clientes asignados
                                    </div>
                                  )}
                                  {(userRole === "4") && (
                                    <div className="clients-count">
                                      {(assignedPersonnel[user.key]?.sellers?.length || 0) +
                                        (assignedPersonnel[user.key]?.buyers?.length || 0)} usuarios asignados
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="body-cell email" data-label="Email">{user.email || 'Email no disponible'}</div>
                              <div className="body-cell telefono" data-label="Teléfono">{user.phone || 'Teléfono no disponible'}</div>
                              <div className="body-cell tipo" data-label="Tipo">
                                <span className={getTypeBadgeClass(user.roleNumber)}>
                                  {user.type}
                                </span>
                              </div>
                              <div className="body-cell estatus" data-label="Estatus">
                                <span className={getStatusBadgeClass(user.status)}>
                                  {user.status}
                                </span>
                              </div>
                              <div className="body-cell accion" data-label="Acciones">
                                <Tooltip title="Editar usuario" placement="left">
                                  <button
                                    className="edit-icon-button"
                                    onClick={(e) => handleEditIconClick(user.userData, e)}
                                    disabled={loading}
                                  >
                                    <EditOutlined />
                                  </button>
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                        </Tooltip>

                        {expandedRows.includes(user.key) && renderExpandedContent(user)}
                      </React.Fragment>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {filteredData.length > 0 && (
            <div className="main-pagination-container">
              <div className="pagination-info">
                <span>
                  {isSearching
                    ? `Mostrando ${filteredData.length} resultado${filteredData.length !== 1 ? 's' : ''} de búsqueda para "${searchText}"`
                    : `Mostrando usuarios ${displayRange.start}-${displayRange.end} de ${filteredData.length}`
                  }
                </span>
              </div>

              <div className="page-size-selector">
                <span>Mostrar:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  disabled={loading}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
                <span>por página</span>
              </div>

              <div className="simple-pagination">
                <Button
                  icon={<LeftOutlined />}
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>

                <div className="current-page-indicator">
                  Página {currentPage}
                </div>

                <Button
                  icon={<RightOutlined />}
                  onClick={handleNextPage}
                  iconPosition="end"
                  disabled={currentPage >= Math.max(1, Math.ceil(filteredData.length / pageSize))}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}

          {isSearching && filteredData.length > 0 && (
            <div className="search-clear-section">
              <Button
                type="link"
                onClick={handleClearSearch}
                disabled={loading}
              >
                Mostrar todos los usuarios
              </Button>
            </div>
          )}
        </div>

        <EditUserModal
          user={userToEdit}
          visible={editModalVisible}
          onClose={handleCloseEditModal}
          onSave={handleSaveEdit}
          currentAdminId={currentAdmin?.id}
          transitionName=""
          maskTransitionName=""
        />
      </div>
    </LayoutPage>
  );
}